/**
 * Production Drift Status Badge — Admin only
 *
 * Polls /api/admin/prod-drift every 60s and renders a compact pill:
 *   • GREEN  — all production canaries passing
 *   • RED    — drift detected (webhook 500, health down, etc.)
 *   • UNKNOWN — never checked yet, or transient network blip
 *
 * Click the pill → forces an immediate re-check via POST /api/admin/prod-drift/check.
 * Hover → tooltip with last-check timestamp + failures, if any.
 *
 * © 2026 A-1 Affiliate Marketing LLC
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const POLL_INTERVAL_MS = 60_000;

const COLOR = {
  green: { bg: 'rgba(31, 138, 76, 0.14)', border: '#1f8a4c', dot: '#2bd17b', label: 'PROD GREEN' },
  red: { bg: 'rgba(201, 56, 56, 0.16)', border: '#c93838', dot: '#ff5252', label: 'PROD RED' },
  unknown: { bg: 'rgba(107, 107, 107, 0.16)', border: '#6b6b6b', dot: '#a8a8a8', label: 'PROD ?' },
};

const fmtAgo = (iso) => {
  if (!iso) return 'never';
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.round(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

export const ProdDriftBadge = () => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hover, setHover] = useState(false);

  const load = useCallback(async (force = false) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const url = force ? `${API}/admin/prod-drift/check` : `${API}/admin/prod-drift`;
      const r = force
        ? await axios.post(url, {}, { headers })
        : await axios.get(url, { headers });
      // /check returns {current_status,...}, /prod-drift returns {status,...}
      const status = r.data?.current_status || r.data?.status || 'unknown';
      setState({
        status,
        checked_at: r.data?.checked_at || r.data?.checked_at,
        last_transition_at: r.data?.last_transition_at,
        failures: r.data?.failures || r.data?.last_failures || [],
        monitored_url: r.data?.monitored_url,
      });
    } catch (e) {
      // 403 = not admin; silently skip rendering
      if (e?.response?.status === 403) setState({ forbidden: true });
    }
  }, []);

  useEffect(() => {
    load(false);
    const id = setInterval(() => load(false), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [load]);

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    await load(true);
    setBusy(false);
  };

  if (!state || state.forbidden) return null;

  const color = COLOR[state.status] || COLOR.unknown;
  const failures = state.failures || [];

  return (
    <div
      data-testid="prod-drift-badge"
      onClick={handleClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={`Click to re-check now${failures.length ? ` — failures: ${failures.join(', ')}` : ''}`}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.4rem 0.85rem',
        borderRadius: '999px',
        background: color.bg,
        border: `1px solid ${color.border}`,
        cursor: busy ? 'wait' : 'pointer',
        fontSize: '0.72rem',
        letterSpacing: '0.12em',
        fontWeight: 600,
        color: '#e8e0cc',
        userSelect: 'none',
        transition: 'opacity 120ms ease',
        opacity: busy ? 0.55 : 1,
      }}
    >
      <span
        data-testid={`prod-drift-dot-${state.status}`}
        style={{
          width: '0.6rem',
          height: '0.6rem',
          borderRadius: '999px',
          background: color.dot,
          boxShadow: state.status === 'red' ? `0 0 8px ${color.dot}` : 'none',
          animation: state.status === 'red' ? 'sb-pulse 1.6s ease-in-out infinite' : 'none',
        }}
      />
      <span>{busy ? 'CHECKING…' : color.label}</span>
      <span style={{ opacity: 0.55, fontWeight: 400, marginLeft: '0.25rem' }}>
        · {fmtAgo(state.checked_at)}
      </span>
      {hover && failures.length > 0 && (
        <div
          data-testid="prod-drift-tooltip"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            background: '#0d1528',
            border: '1px solid #c9a84c',
            borderRadius: '4px',
            padding: '0.6rem 0.85rem',
            fontSize: '0.7rem',
            letterSpacing: '0.04em',
            color: '#e8e0cc',
            whiteSpace: 'nowrap',
            zIndex: 50,
            pointerEvents: 'none',
            fontWeight: 400,
          }}
        >
          <div style={{ color: '#c9a84c', marginBottom: '0.25rem' }}>
            Failing canaries:
          </div>
          {failures.map((f, i) => (
            <div key={i} style={{ fontFamily: 'monospace' }}>· {f}</div>
          ))}
        </div>
      )}
      <style>{`@keyframes sb-pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.55; transform: scale(1.15); }
      }`}</style>
    </div>
  );
};

export default ProdDriftBadge;
